import type { CatalogItem, GlobalConfig, SpecItem } from '../types';
import type { PricingCalculations } from './pricing';
import { findCatalogBodyColorItem, BODY_COLOR_IMAGES } from '../utils/xmlParser';

/**
 * Resolves the human-readable model/material/door-color names for a kitchen
 * config, joining multiple selected combinations with " & ". Shared between
 * AppContext (current editing view) and ProposalPreview (per-unit bulk print),
 * so both stay in sync — never duplicate this logic.
 */
export function resolveConfigNames(config: GlobalConfig) {
  const join = (primary: string, extra: string[] | undefined, custom: string, fallback: string) => {
    const rawList = [primary, ...(extra || [])].filter(Boolean).map(v =>
      v === 'CUSTOM' ? (custom || fallback) : v
    );
    return Array.from(new Set(rawList)).join(' & ') || '';
  };

  return {
    resolvedModelName: join(config.model, config.extraModels, config.customModel, 'Özel Model'),
    resolvedMaterialName: join(config.material, config.extraMaterials, config.customMaterial, 'Özel Malzeme'),
    resolvedDoorColor: join(config.doorColor, config.extraDoorColors, config.customDoorColor, 'Özel Renk'),
    resolvedBodyColor: join(config.bodyColor, config.extraBodyColors, '', 'Özel Gövde Rengi'),
    resolvedGlassType: join(config.glassType, config.extraGlassTypes, '', ''),
    resolvedProfileColor: join(config.profileColor, config.extraProfileColors, '', ''),
  };
}

/** Every body-color slot of a config, primary first, deduplicated and without empty slots. */
export function collectBodyColors(config: Pick<GlobalConfig, 'bodyColor' | 'extraBodyColors'>): string[] {
  return Array.from(new Set([config.bodyColor, ...(config.extraBodyColors || [])].filter(Boolean)));
}

const isBodyColorItem = (item: SpecItem) => item.category === 'Gövde Rengi' || item.category === 'Gövde';

/**
 * Rewrites the 'Gövde Rengi' rows of a spec list so they match the body colors
 * selected in Mutfak Konfigürasyonu — one row per color, each carrying the
 * catalog (or Cloudinary fallback) image so the visual attachment cards fill in
 * automatically. Rows are reused by position, so ids and any manually attached
 * image survive a rename; surplus rows are dropped when a color is removed.
 * Returns `prev` unchanged when nothing differs — the caller is a React effect
 * and a fresh array on every run would loop.
 */
export function syncBodyColorSpecItems(
  prev: SpecItem[],
  bodyColors: string[],
  catalog: CatalogItem[],
): SpecItem[] {
  const existing = prev.filter(isBodyColorItem);

  if (bodyColors.length === 0) {
    return existing.length === 0 ? prev : prev.filter(item => !isBodyColorItem(item));
  }

  const nextBodyItems: SpecItem[] = bodyColors.map((colorName, idx) => {
    const catalogItem = findCatalogBodyColorItem(colorName, catalog);
    const code = catalogItem?.code || colorName;
    const name = catalogItem?.name || `${colorName} Gövde Rengi`;
    const autoImage = catalogItem?.image || BODY_COLOR_IMAGES[colorName] || null;

    const current = existing[idx];
    if (!current) {
      return { id: `body_color_${Date.now()}_${idx}`, category: 'Gövde Rengi', code, name, image: autoImage };
    }
    const image = autoImage || current.image || null;
    if (current.code === code && current.name === name && current.image === image) return current;
    return { ...current, category: current.category, code, name, image };
  });

  const unchanged = existing.length === nextBodyItems.length &&
    nextBodyItems.every((item, idx) => item === existing[idx]);
  if (unchanged) return prev;

  // Keep the block where it already sits; a first-time insert lands right after
  // the Kapak Rengi rows (visual attachment order: kapak → gövde → gerisi).
  let insertIndex = prev.findIndex(isBodyColorItem);
  if (insertIndex < 0) {
    let lastDoorIndex = -1;
    prev.forEach((item, i) => {
      if (item.category === 'Renk' || item.category === 'Kapak Rengi') lastDoorIndex = i;
    });
    insertIndex = lastDoorIndex + 1;
  }
  const restInsertIndex = prev.slice(0, insertIndex).filter(item => !isBodyColorItem(item)).length;

  const updated = prev.filter(item => !isBodyColorItem(item));
  updated.splice(restInsertIndex, 0, ...nextBodyItems);
  return updated;
}

/** Visual attachment card deck ordering: colors first, then body color, then everything else. */
export function sortVisualAttachmentItems(specItems: SpecItem[]): SpecItem[] {
  const getCategoryRank = (cat: string) => {
    if (cat === 'Renk' || cat === 'Kapak Rengi') return 1;
    if (cat === 'Gövde Rengi' || cat === 'Gövde') return 2;
    return 3;
  };
  return [...specItems].sort((a, b) => getCategoryRank(a.category) - getCategoryRank(b.category));
}

export interface PreviewRow {
  key: string;
  displayName: string;
  listPrice: number;
  campaignDiscount: number | '';
  extraDiscount: number | '';
  vatRate: number;
  incVatPrice: number;
}

/**
 * Builds the quoting-summary rows (Mutfak Dolapları, Aksesuarlar, Cihaz,
 * Eviye, Armatür, ...) and the discount-savings totals shown on the printed
 * proposal, from a full PricingCalculations result.
 */
export function buildPreviewPricing(pricingCalculations: PricingCalculations) {
  const find = (key: string) => pricingCalculations.rows.find(r => r.key === key)!;
  const modelCabinets = find('model_cabinets');
  const internalAccessories = find('internal_accessories');
  const externalAccessories = find('external_accessories');
  const shippingAssembly = find('shipping_assembly');
  const countertop = find('countertop');
  const appliances = find('appliances');
  const sinks = find('sinks');
  const taps = find('taps');

  const previewRows: PreviewRow[] = [];

  const pushIfPriced = (row: typeof modelCabinets) => {
    if (row.listPrice !== '' && row.listPrice > 0) {
      previewRows.push({
        key: row.key,
        displayName: row.displayName,
        listPrice: row.listPrice,
        campaignDiscount: row.campaignDiscount,
        extraDiscount: row.extraDiscount,
        vatRate: row.vatRate === '' ? 0 : row.vatRate,
        incVatPrice: row.incVatPrice,
      });
    }
  };

  pushIfPriced(modelCabinets);
  pushIfPriced(internalAccessories);
  pushIfPriced(externalAccessories);
  pushIfPriced(shippingAssembly);
  pushIfPriced(countertop);
  pushIfPriced(appliances);
  pushIfPriced(sinks);
  pushIfPriced(taps);
  pricingCalculations.rows.filter(r => r.isManual).forEach(pushIfPriced);

  const hasExtraDiscount = previewRows.some(row => row.listPrice !== 0 && typeof row.extraDiscount === 'number' && row.extraDiscount > 0);
  const modelCabinetsRow = pricingCalculations.rows.find(r => r.key === 'model_cabinets')!;
  const cabinetsCampaignSavings = (modelCabinetsRow.listPrice === '' ? 0 : modelCabinetsRow.listPrice as number) - modelCabinetsRow.campaignPrice;
  const otherRowsDiscountSavings = pricingCalculations.rows
    .filter(row => row.key !== 'model_cabinets')
    .reduce((sum, row) => {
      const v = row.listPrice === '' ? 0 : row.listPrice as number;
      return sum + (v - row.campaignPrice);
    }, 0);

  return { previewRows, hasExtraDiscount, cabinetsCampaignSavings, otherRowsDiscountSavings };
}
